import { useGroupBySelector } from '@app/src/context/GroupBySelectorContext'
import ChartTypeSelector, { ChartType } from '@app/src/pages/Assessments/ChartTypeSelector'
import { AnswersGroupingType } from '@app/src/pages/ResourceCollection/Filters/StatisticsConstants'
import { QuestionTypes } from '@app/src/wf-constants'
import React, { useEffect } from 'react'
import type { StatisticsForQuestionPerGroup } from '../../DataHubScene'

interface ChartTypeSelectorProps {
  selectedChartType: ChartType
  setSelectedChartType: React.Dispatch<React.SetStateAction<ChartType>>
  statistics: StatisticsForQuestionPerGroup
}

const getPossibleChartTypes = (statistics: StatisticsForQuestionPerGroup, groupBy: AnswersGroupingType) => {
  const barChartTypes = [ChartType.Vertical, ChartType.Horizontal]
  const isGroupByPeriodName = groupBy === AnswersGroupingType.PeriodName
  const isSinglePeriod = isGroupByPeriodName && statistics.resultForGroups.length === 1

  const isNumberQuestion = statistics.questionType.toLowerCase() === QuestionTypes.Number.toLowerCase()
  const isOptionQuestion = statistics.questionType.toLowerCase() === QuestionTypes.Options.toLowerCase()

  if (!isGroupByPeriodName) {
    return { possible: barChartTypes, default: ChartType.Horizontal }
  }

  if (isNumberQuestion) {
    return { possible: [ChartType.Line], default: ChartType.Line }
  }

  if (!isOptionQuestion) {
    return { possible: barChartTypes, default: ChartType.Vertical }
  }

  if (isSinglePeriod) {
    return { possible: [ChartType.Pie, ...barChartTypes], default: ChartType.Pie }
  }

  return { possible: barChartTypes, default: ChartType.Vertical }
}

const DataHubChartTypeSelector: React.FC<ChartTypeSelectorProps> = ({
  selectedChartType,
  setSelectedChartType,
  statistics,
}) => {
  const { groupBy } = useGroupBySelector()
  const { possible: possibleChartTypes, default: defaultChartType } = getPossibleChartTypes(statistics, groupBy)

  useEffect(() => {
    setSelectedChartType(defaultChartType)
  }, [defaultChartType, setSelectedChartType])

  return (
    <ChartTypeSelector
      setSelectedChartType={setSelectedChartType}
      selectedChartType={selectedChartType}
      allowedChartTypes={possibleChartTypes}
    />
  )
}

export default DataHubChartTypeSelector
